import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { WarningsService } from 'app/data-studio-redesign/services/warnings.service';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { DataStudioGranularityType, DataStudioQueryType } from 'app/shared/services/data-services/graphql';
import { map, takeUntil } from 'rxjs/operators';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import {SignalSelectionCacher} from '../signal-selection-cacher/signal-selection-cacher';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import * as locale from 'ngx-bootstrap/locale';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
    selector: 'app-data-turbine-log',
    templateUrl: './data-turbine-log.component.html',
    styleUrls: ['./data-turbine-log.component.scss'],
    standalone: false
})
export class DataTurbineLogComponent implements OnInit, OnDestroy {

  public bsConfig;
  public bsRangeValue: Date[];
  public minDate = new Date('01-01-2019');
  public maxDate = new Date();
  public theme$: Observable<any>;
  public selectedAggregation = 'park';
  public dataSelectOptions: any = {};
  public dataParks: Array<any>;
  public parkSelected;
  public turbinesSelected = [];
  public turbineTypes: Array<any> = [];
  public turbineType;
  public turbineTypeByPark: Array<any> = [];
  public turbineWithCodeArray: Array<any> = [];
  public assetParkTurbinesSelected: Array<any> = [];
  public assetParks: Array<any> = [];
  public parkNameSelected;
  public signalGroupArray: Array<any> = [];
  public totalSignals = 0;
  public totalSignalsSelected = 0;
  public selectAllSignalsFlag = false;
  public tLogSignalGroupSelected = 0;
  private enumQueryType = DataStudioQueryType;
  public enumGranularity = DataStudioGranularityType;
  private unsubscribe$ = new Subject();
  public turbineTypeSelection;
  public wrongDate = false;
  public selectedAggregationName: string;
  public aggregationOptions: { values: string[], titles: string[] } = {
    values: ['park', 'turbine_type', 'turbine'],
    titles: ['COMMON.PARK', 'DATA_STUDIO.DATA_PARAMETERS.TURBINE_TYPE', 'COMMON.TURBINE']
  };
  public dataParksNames: string[];
  public assetParksNames: Array<string>;
  public turbineSelection: string;
  public turbinesList: string[];
  private unsubscriber = new ReplaySubject();

  constructor(private dataStudioService: DataStudioRedesignService,
              private warningService: WarningsService,
              private themeService: ThemeService,
              private dataStudioHelper: DataStudioHelperService,
              private selectedSignalsCacher: SignalSelectionCacher,
              private translateService: TranslateService,
              private localeService: BsLocaleService,
							private dateTimeFormatService: DateTimeFormatService,
              private languageService: LanguageService) {
              this.selectedAggregationName = this.aggregationOptions.titles[0];
              this.bsRangeValue = [new Date(), new Date()];
              this.initSelectOptions();
  }

  ngOnInit(): void {
    this.subscribeLanguageChange();
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, { containerClass: theme, rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(), dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase() }))
    );
    this.getAllParkAndTurbines();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  public changeDateRange(event) {
    if (event) {
      this.wrongDate = this.dataSelectOptions.fileRequest.wrongDate = Math.abs(event[0].getTime() - event[1].getTime()) / 1000 / 60 / 60 / 24 > 365 ? true : false;
      const dateA = new Date(event[0].getTime());
      this.dataSelectOptions.dateFrom = Number(dateA.setHours(0, 0, 0, 0));
      this.dataSelectOptions.fileRequest.startTS = this.dataSelectOptions.dateFrom;
      const dateB = new Date(event[1].getTime());
      this.dataSelectOptions.dateTo = Number(dateB.setHours(23, 59, 59, 999));
      this.dataSelectOptions.fileRequest.endTS = this.dataSelectOptions.dateTo;
      if (this.dataSelectOptions.fileRequest.signals !== undefined) {
        this.calculateSize();
      }
    }
  }

  public selectAggregation(event) {
    if (this.selectedAggregationName !== event.option) {
      this.selectedAggregationName = event.option
      this.selectedAggregation = this.aggregationOptions.values[event.index];
      this.parkSelected = null;
      this.parkNameSelected = null;
      this.turbineType = undefined;
      this.assetParkTurbinesSelected = [];
      switch (this.selectedAggregation) {
        case 'park':
          this.dataSelectOptions.aggregationLevel = '0';
          this.selectPark();
          break;
        case 'turbine':
          this.parkSelected = this.dataParks[0];
          this.turbineWithCodeArray = this.parkSelected.turbines.map(e => {
            return {
              ...e,
              displayName: `${this.parkSelected.fe_name} - ${e.code.replaceAll('_', ' ')}`,
              value: e.serialNav, parkCode: this.parkSelected.code, name: e.name, id: e._id, parkName: this.parkSelected.name, code: e.code
            };
          });
          this.dataSelectOptions.aggregationLevel = '1';
          this.selectedOneTurbine({option: this.turbineWithCodeArray[0].displayName});
          this.setTurbinesList();
          break;
        case 'turbine_type':
          this.dataSelectOptions.aggregationLevel = '2';
          this.selectTurbineType({option: this.turbineTypes[0]});
          break;
      }
    }
  }

  public selectTurbineType({option}) {
    this.turbineTypeSelection = option;
    this.turbinesSelected = [];
    this.getSignalsByTurbineType(option);
    this.turbineType = option;
    this.filterAssetsByTurbineType();
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.calculateSize();
  }

  public selectPark(event = null) {
    this.turbinesSelected = [];
    this.parkSelected = event ? this.dataParks[event.index] : this.dataParks[0];
    if (this.selectedAggregation === 'turbine') {
      this.turbineWithCodeArray = this.parkSelected.turbines.map(e => {
        return {
          ...e,
          displayName: `${this.parkSelected.fe_name} - ${e.code.replaceAll('_', ' ')}`,
          value: e.serialNav, parkCode: this.parkSelected.code, name: e.name, id: e._id, parkName: this.parkSelected.name, code: e.code
        };
      });
      this.selectedOneTurbine({option: this.turbineWithCodeArray[0].displayName});
      this.setTurbinesList();
    } else {
      this.getAvailableTSForPark(this.parkSelected.name);
      this.getSignalsByParkName(this.parkSelected.name);
      this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare);
      this.dataSelectOptions.assetPark = this.parkSelected.code;
      this.dataStudioService.sendTurbinesToPreview(this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare));
    }
  }

  public selectedOneTurbine({option}) {
    const turbine = this.turbineWithCodeArray.find(item => item.displayName === option);
    if (turbine) {
      this.turbineSelection = turbine.displayName;
      this.getAvailableTSForPark(turbine.parkName);
      this.getSignalsByTurbineName(turbine.name);
      this.dataSelectOptions.assetPark = turbine.parkCode;
      this.dataSelectOptions.assetSelected = turbine.value;
      this.dataSelectOptions.fileRequest.turbines.push(turbine.id);
      this.calculateSize();
    }
  }

  public selectedAssetTurbines(event) {
    if (!this.turbinesSelected.find(data => data.parkCode === event.parkCode)) {
      this.turbinesSelected.push(event);
    } else {
      this.turbinesSelected[(this.turbinesSelected.findIndex(park => park.parkCode === event.parkCode))].turbines = event.turbines;
    }
    const onlyTurbines = [];
    this.turbinesSelected.forEach(element => {
      element.turbines.forEach(turbine => {
        onlyTurbines.push(turbine._id);
      });
    });
    this.dataSelectOptions.fileRequest.turbines = onlyTurbines;
    this.calculateSize();
  }

  public filterAssetsByTurbineType() {
    this.assetParkTurbinesSelected = [];
    const filteredArray: Array<any> = [];
    this.dataParks.forEach(park => {
      park.turbines.forEach(turbine => {
        if (turbine.type === this.turbineType && !filteredArray.includes(park)) {
          filteredArray.push(park);
        }
      });
    });
    this.assetParks = filteredArray;
    this.setAssetNames();
  }

  private getSignalsByTurbineType(turbineType: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
    this.checkSelectedSignals();
  }

  private getSignalsByParkName(parkName: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
    this.checkSelectedSignals();
  }

  private getSignalsByTurbineName(turbineName: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
    this.checkSelectedSignals();
  }

  private resetSignalArray() {
    this.dataSelectOptions.fileRequest.signals = [];
    this.dataSelectOptions.fileRequest.turbines = [];
    this.totalSignalsSelected = 0;
    this.selectAllSignalsFlag = false;
    this.totalSignals = this.tLogSignalGroupSelected = 0;
    this.signalGroupArray = [];
    this.signalGroupArray.push({
      signalGroupName: { checked: false, name: 'DATA_STUDIO.SIGNALS.ALL_DATA_FIELDS' },
      signals: [
        { checked: false, name: 'ALARMS.CODE', value: 'code', description: 'ALARMS.ALARM_CODE' },
        { checked: false, name: 'ALARMS.DESCRIPTION', value: 'description', description: 'ALARMS.ALARM_DESC' },
        { checked: false, name: 'ALARMS.CREATED', value: 'createTS', description: 'ALARMS.CREATED' },
        { checked: false, name: 'ALARMS.TYPE', value: 'type', description: 'ALARMS.ALARM_TYPE' }
        ]
    });
    this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
    this.dataStudioService.sendSignalsToPreview(this.signalGroupArray.flatMap(e => e.signals));
  }

  private getAllParkAndTurbines() {
    this.dataStudioService.getAllParkAndTurbines().subscribe(response => {
      if (response) {
        this.dataParks = JSON.parse(JSON.stringify(response));
        this.assetParks = JSON.parse(JSON.stringify(response));
        this.dataParks.sort((a, b) => {
          return a.fe_name.localeCompare(b.fe_name);
        });
        this.assetParks.sort((a, b) => {
          return a.fe_name.localeCompare(b.fe_name);
        });
        this.dataParksNames = this.dataParks.map(park => park.fe_name);
        this.setAssetNames();
        this.getTurbineTypes(this.dataParks);
        this.getTurbineWithCode(this.dataParks);
        this.initSelectOptions();
        this.selectTurbineType(this.turbineTypes[0]);
        this.selectPark();
      }
    });
  }

  private getTurbineTypes(array: Array<any>) {
    array.forEach(element => {
      element.turbines.forEach(turbine => {
        if (!this.turbineTypes.includes(turbine.type)) {
          this.turbineTypes.push(turbine.type);
        }
      });
    });
    this.turbineTypes.sort();
  }

  private getTurbineWithCode(array: Array<any>) {
    array.forEach(element => {
      element.turbines.sort(this.dataStudioHelper.naturalCompare);
      element.turbines.forEach(turbine => {
        this.turbineWithCodeArray.push({ displayName: element.name + ' - ' + turbine.code.replace('_', ' ').replace('_', ' '), value: turbine.serialNav, parkCode: element.code, name: turbine.name, id: turbine._id, parkName: element.name });
      });
    });
    this.setTurbinesList();
  }

  public addPark() {
    const selectedPark = this.assetParks.find(park => park.fe_name === this.parkNameSelected);
    this.getAvailableTSForPark(selectedPark.name);
    this.assetParkTurbinesSelected.push(selectedPark);
    this.assetParkTurbinesSelected.forEach(element => {
      element.turbines.sort(this.dataStudioHelper.naturalCompare);
    });
    this.assetParks = this.assetParks
      .filter((park) => park.fe_name !== this.parkNameSelected)
      .sort();
    this.setAssetNames();
    this.dataStudioService.sendTurbinesToPreview(this.assetParkTurbinesSelected.flatMap(e => e.turbines));
    this.parkNameSelected = null;
  }

  public selectParkAsset(event) {
    this.parkNameSelected = event.option;
  }

  public removeParkList(event) {
    this.assetParkTurbinesSelected = this.assetParkTurbinesSelected.filter(
      (park) => park.name !== event.parkName
    );
    this.assetParks.push(this.dataParks.find(park => park.name === event.parkName));
    this.assetParks.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    event.turbines.forEach(turbine => {
      for (let i = 0; i < this.dataSelectOptions.fileRequest.turbines.length; i++) {
        if (turbine._id === this.dataSelectOptions.fileRequest.turbines[i]) {
          this.dataSelectOptions.fileRequest.turbines.splice(i, 1);
        }
      }
    });
    this.dataStudioService.sendTurbinesToPreview(this.assetParkTurbinesSelected.flatMap(e => e.turbines));
    this.calculateSize();
  }

  public calculateSize() {
    const days = this.warningService.getDays(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
    const columns = 5 + (this.dataSelectOptions.fileRequest.signals.length);  // Right now 5 fields: Date UTC, Turbine Timezone date, User Timezone date, Turbine Code, Turbine Serial
    let rows = this.warningService.getRowsNumber(this.dataSelectOptions.granularity, days);
    let fields = this.warningService.getFieldsNumber(this.dataSelectOptions.granularity, columns, days);
    if (this.dataSelectOptions.aggregationLevel === '0' || this.dataSelectOptions.aggregationLevel === '2') { // ParkLevel  -> we multiply rows & fields for the number of turbines before getting the file size
        let turbines = 0;
        this.dataSelectOptions.fileRequest.turbines.forEach(element => {
          turbines++;
        });
        rows = rows * turbines;
        fields = fields * turbines;
        this.dataSelectOptions.estimatedRows = rows;
        this.dataSelectOptions.estimatedFields = fields;
        this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    } else {
      this.dataSelectOptions.estimatedRows = rows;
      this.dataSelectOptions.estimatedFields = fields;
      this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    }
    this.dataStudioHelper.setDateObservable$(this.dataSelectOptions);
  }

  private initSelectOptions() {
    this.dataSelectOptions.aggregationLevels = ['park', 'turbine', 'turbine type'];
    this.dataSelectOptions.aggregationLevel = '0';
    this.dataSelectOptions.assetSelected = null;
    this.dataSelectOptions.assetPark = null;
    this.dataSelectOptions.dateFrom = new Date().setHours(0, 0, 0, 0);
    this.dataSelectOptions.dateTo = new Date().getTime();
    this.dataSelectOptions.granularities = ['10 minutes', '1 hour', '1 day', '1 Month', '1 year', 'Request 30 second data'];
    this.dataSelectOptions.granularity = 0;
    this.dataSelectOptions.selectableColumns = [];
    this.dataSelectOptions.selectableColumnsNames = [];
    this.dataSelectOptions.selectableUnits = [];
    this.dataSelectOptions.selectedColumns = [];
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.dataSelectOptions.fileRequest = {
      fileName: '',
      turbines: [],
      signals: [],
      granularity: this.enumGranularity.G10M,
      queryType: this.enumQueryType.Tlog,
      fileType: '',
      startTS: Number(this.dataSelectOptions.dateFrom),
      endTS: Number(this.dataSelectOptions.dateTo)
    };
  }

  public selectAllSignalsByGroup(groupName) {
    this.signalGroupArray.forEach(element => {
      if (element.signalGroupName.name === groupName) {
        element.signals.forEach(signal => {
          signal.checked = element.signalGroupName.checked;
        });
      }
    });
    this.checkSelectedSignals();
  }

  public checkSelectedSignals(): void {
    this.dataSelectOptions.fileRequest.signals = [];
    let signals = 0;
    this.signalGroupArray.forEach(element => {
      element.signals.forEach(signal => {
        if (signal.checked) {
          signals++;
          if (!this.dataSelectOptions.fileRequest.signals.includes(signal.value)) {
            this.dataSelectOptions.fileRequest.signals.push(signal.value);
          }
        }
      });
      switch (element.signalGroupName.name) {
        case 'DATA_STUDIO.SIGNALS.ALL_DATA_FIELDS':
          const signalsOk = element.signals.filter(data => data.checked === true);
          this.tLogSignalGroupSelected = signalsOk.length;
          element.signalGroupName.checked = element.signals.length === this.tLogSignalGroupSelected ? true : false;
          break;
      }
    });
    this.totalSignalsSelected = signals;
    this.selectAllSignalsFlag = this.totalSignalsSelected === this.totalSignals ? true : false;
    this.calculateSize();
  }


  private getAvailableTSForPark(parkName) {
    this.dataStudioService.getAvailableTS(parkName).pipe(takeUntil(this.unsubscribe$)).subscribe(data => {
      if (data) {
        this.minDate = new Date(data);
      }
    });
  }

  private setAssetNames() {
    this.assetParksNames = this.assetParks.map(asset => asset.fe_name);
  }

  private setTurbinesList() {
    this.turbinesList = this.turbineWithCodeArray.map(turbine => turbine.displayName);
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
        this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
      }
    });
  }
  private sortTranslateSignals(signalGroupArray){
    return signalGroupArray.map(item => ({
      ...item,
      signals: item.signals.sort((a,b) => {
        const translatedA = this.translateService.instant(a.name) || a.name;
        const translatedB = this.translateService.instant(b.name) || b.name;
        return translatedA.localeCompare(translatedB);
      })
    }))
  }
}

